import {Component, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ToastService} from "../../../services/Toast.service";
import {NumeralService} from "../../../services/Numeral.service";
import {SwitchBoard} from "../../../services/SwitchBoard";
import {Router} from "@angular/router";
import {LoadingService} from "../../../services/LoadingService";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import {takeUntil} from "rxjs/operators";
import {Session} from "../../../services/Session";
import {environment} from "../../../../environments/environment";
import {PageTitleService} from "../../../services/PageTitle";

declare const _: any;

@Component({
  selector: 'app-user-history-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './user-history-report.component.html',
  styleUrl: './user-history-report.component.scss'
})
export class UserHistoryReportComponent implements OnInit, OnDestroy {
  userTableData: any = {};
  org: string = Session.getOrg();
  user: any = {};
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  userHistoryReportSort: any = {};
  sortColumnName: any;
  sortColumnOrder: any;
  columns: Array<any> = [];
  hasUserData: boolean = false;
  showFeedback = false;
  feedbackFilterData = {};
  userHistoryData: any;
  currentEnvironment: any;
  orgName: string;

  constructor(
    private _router: Router,
    private titleService: PageTitleService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private switchBoard: SwitchBoard,
    private taxPlanningService: TaxPlanningService,
    private numeralService: NumeralService,
  ) {}

  ngOnInit() {
    this.titleService.setPageTitle('User History Report');
    this.user = Session.getUser();
    this.orgName = this.user.org || '';
    this.currentEnvironment = environment;
    this.getUsersHistoryReportData();
    this.switchBoard.onToggleFeedback.next({
      display: true,
      pageName: 'User History Report Page'
    });
    this.switchBoard.onPageChangeListener.next({});
    this.switchBoard.onClickPrev.pipe(takeUntil(this.destroy$)).subscribe(obj => {
      this._router.navigate(['tax-planning/reports']);
    });
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.switchBoard.onToggleFeedback.next({
      display: false
    });
  }

  getUsersHistoryReportData(sortColumn?, sortOrder?){
    const base = this;
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getUserHistoryReportData(this.org, this.user.id, sortColumn, sortOrder)
      .subscribe((response) => {
        this.loadingService.triggerLoadingEvent(false);
        this.userHistoryData = response;
        if (!sortColumn) {
          this.formatColumnsForTable(response.columns);
        }
        this.getFormattedData(response.data);
        base.hasUserData = true;
      }, (error) => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to get user history report.');
      });
  }

  getFormattedData(data) {
    const base = this;
    if (data && data.length > 0) {
      _.each(data, function(item){
        item.totalTaxesSaved = base.numeralService.format('$0,0', item.totalTaxesSaved);
        item.totalPublishedTaxesSaved = base.numeralService.format('$0,0', item.totalPublishedTaxesSaved);
        item.actualTaxesSaved = base.numeralService.format('$0,0', item.actualTaxesSaved);
        if (item.hashTagClicks) {
          item = _.merge(item, item.hashTagClicks);
        }
      });
    }
    this.userTableData = data;
  }

  formatColumnsForTable(data) {
    const base = this;
    const colsArray = [];
    let orderArray;
    if (typeof data !== 'string') {
      orderArray = data;
    } else {
      orderArray = JSON.parse(data);
    }
    _.each(Object.keys(orderArray), function(key) {
      if (key != 'hashtagClicks') {
        colsArray.push({name: orderArray[key], title: key, type: 'normal', classes: 'pointer zero-overflow', sortable: true});
      } else {
        base.formatColsForHashTagClicks(orderArray[key], colsArray);
      }
    });
    this.columns = colsArray;
  }

  formatColsForHashTagClicks(keysData, colsArray) {
    if (keysData && keysData.myArrayList && keysData.myArrayList.length > 0) {
      _.each(keysData.myArrayList, function(col){
        colsArray.push({name: col, title: col, type: 'normal', classes: 'pointer zero-overflow', sortable: true});
      });
    }
  }
  columnSort(column, columns) {
    if (column.sortable) {
      let order = this.getSortType(column);
      this.userHistoryReportSort[column.name] = order;
      this.sortColumnName = column.name;
      this.sortColumnOrder = order;
      this.getUsersHistoryReportData(column.name, order);
      this.resetSortOrder(column, columns);
    }
  }

  resetSortOrder(column, columns) {
    _.each(columns, function(col){
      if (col.name !== column.name) {
        col.sortType = '';
      }
    });
  }

  getSortType(column){
    if (!column.sortType){
      column.sortType = 'asc';
    } else if (column.sortType == 'asc'){
      column.sortType = 'desc';
    } else if (column.sortType == 'desc'){
      column.sortType = 'asc';
    }
    return column.sortType;
  }

  openInstancePage(rowData) {
    if (rowData && rowData.firmId) {
      const firmUrl = 'https://' + rowData.firmId + '.taxplaniq.com/login';
      window.open(firmUrl);
    }
  }

  showFeedbackReport(rowData, column) {
    if (rowData[column.name]) {
      if (column.name == 'submittedBugs') {
        this.feedbackFilterData['type'] = 'Issue';
      } else if (column.name == 'submittedEnhancements') {
        this.feedbackFilterData['type'] = 'Enhancement';
      } else if (column.name == 'submittedHelps') {
        this.feedbackFilterData['type'] = 'Help';
      }
      this.feedbackFilterData['firmId'] = rowData.firmId || '';
      this.showFeedback = true;
    }
  }

  exportToExcel() {
    let urlString = (this.currentEnvironment.production) ? 'https://services.taxplaniq.com' : 'https://dev-services.taxplaniq.com';
    let getUrl = urlString + '/TaxPlanningServices/orgs/' + this.org + '/users/' + this.user.id + '/reports/user-login-history';
    if (this.sortColumnName) {
      getUrl += `?sortKey=${this.sortColumnName}&sortOrder=${this.sortColumnOrder}`;
    }
    const data = {
      url : getUrl,
      fileName: `User - History - Report.xls`,
      reportName: 'user_history',
      orgName: this.orgName,
    };
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.exportROIExcel(this.org, this.user.id, data)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `User - History - Report.xls`;
        link.click();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed To Export Into Excel');
      });
  }

}
